import { LOADING_STATUSES } from 'constants/loadingStatuses'

import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit'
import { getFilesSdk } from 'sdk/files'
import { OrderType } from '@cloudike/web_ui_components'
import { CopyFileExtraParamsType, IDestinationParams, IFsNodeSchema, NodeTypes } from '@cloudike/web_files'
import { hideGlobalProgressLoader, showGlobalProgressLoader } from 'features/common/app-progress-bar'
import { NOTIFICATION_TYPES, showNotification } from 'features/common/notifications'
import { t } from 'i18next'
import { RootState } from 'store'
import { getNewFileName, getNewFolderName, isFileExist, isFolderExist } from 'features/files/filesUtils'
import { getHighestLevelErrorByFieldName } from 'utils/utils'

import { publicLinkFilesActions } from '../publicLinkFilesSlice'

export enum SortColumns {
  NAME = 'name',
  MODIFIED = 'updated',
  SIZE = 'file_info.size',
}

const adapter = createEntityAdapter<IFsNodeSchema>()

export const publicLinkCopyMoveModalSelectors = adapter.getSelectors()

const initialState = {
  status: LOADING_STATUSES.IDLE,
  error: null,
  sort: { by: SortColumns.NAME, direction: OrderType.ASK },
  selectedItemsIds: [],
  renamingItemId: null,
  currentFolderId: '',
  breadcrumbs: [{ name: '', id: '' }]
}

export const fetchCopyMoveNodesThunk = createAsyncThunk(
  'publicLinkCopyMoveModal/fetchCopyMoveNodesThunk',
  async ({ parentId = '' }: { parentId?: string }, { getState }) => {
    const state = getState() as RootState
    const href = state.publicLinkFiles.itemsHref
    const token = state.publicLinkFiles.token
    const sdk = getFilesSdk()

    const nodesResponse = await sdk.publicLinksService.getAllNotMyFsNodesByLink(href, { preview: true, preview_jwt: true, embedded: true, parent_id: parentId }, 500, undefined, token)

    return nodesResponse
  }
)

export const createCopyMoveFolderThunk = createAsyncThunk(
  'publicLinkCopyMoveModal/createCopyMoveFolderThunk',
  async ({ name, parentId, callback }: { name: string, parentId: string, callback: () => void }, { dispatch, getState }) => {
    const state = getState() as RootState

    const nodesHref = state.publicLinkFiles.itemsHref
    const idsHref = state.publicLinkFiles.idsHref

    showGlobalProgressLoader()

    try {
      const sdk = getFilesSdk()

      if (isFolderExist(publicLinkCopyMoveModalSelectors.selectAll(state.publicLinkCopyMoveModal), name)) {
        showNotification({
          type: NOTIFICATION_TYPES.WARNING,
          title: t('l_notification_folderNameError'),
          message: t('l_notification_createFolderError')
        })

        return
      }

      const response = await sdk.publicLinksService.createNotMyFsNodeByLink(nodesHref, idsHref,
        { type: NodeTypes.DIR, name, parent_id: parentId }
      )

      dispatch(actions.addFolder(response.data))
      callback()
    } catch (error) {

    } finally {
      hideGlobalProgressLoader()
    }
  }
)

export const copyItemsThunk = createAsyncThunk(
  'publicLinkCopyMoveModal/copyItemsThunk',
  async ({ items, parentId }: { items: IFsNodeSchema[], parentId: string }, { getState }) => {
    showGlobalProgressLoader()
    const state = getState() as RootState

    const copyDirHref = state.publicLinkFiles.copyDirHref
    const shareId = state.publicLinkFiles.sharedId
    const token = state.publicLinkFiles.token
    const currentItems = publicLinkCopyMoveModalSelectors.selectAll(state.publicLinkCopyMoveModal)

    try {
      const sdk = getFilesSdk()

      await Promise.all(items.map(async item => {
        if (item.type === NodeTypes.DIR) {
          let newItemName = item.name

          if (isFolderExist(currentItems, item.name)) {
            newItemName = getNewFolderName(currentItems, item.name)
          }

          const params: IDestinationParams = {
            source: item._links.self.href,
            destination: {
              parent_id: parentId
            }
          }

          if (newItemName) {
            params.destination.name = newItemName
          }

          await sdk.publicLinksService.copySharedWithMeDir(copyDirHref, params, token)
        }

        if (item.type === NodeTypes.FILE) {
          let newItemName = item.name

          if (isFileExist(currentItems, item.name)) {
            newItemName = getNewFileName(currentItems, item.name)
          }

          const params: CopyFileExtraParamsType = {
            name: newItemName
          }

          await sdk.publicLinksService.copySharedWithMeByShareId(item.id, parentId, shareId, params, token)
        }
      }))

      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: t('l_notification_itemsCopied', { number: items.length })
      })
    } catch (error) {
      console.log(error)
      if (getHighestLevelErrorByFieldName(error, 'code') === 'SizeQuotaExceeded') {
        showNotification({
          type: NOTIFICATION_TYPES.WARNING,
          message: t('l_notification_copyFilesError'),
          title: t('l_notification_outOfStorage'),
          typeError: 'SizeQuotaExceeded',
        })
      }
    } finally {
      hideGlobalProgressLoader()
    }
  }
)

export const moveItemsThunk = createAsyncThunk(
  'publicLinkCopyMoveModal/moveItemsThunk',
  async ({ items, parentId }: { items: IFsNodeSchema[], parentId: string }, { dispatch, getState }) => {
    showGlobalProgressLoader()

    const state = getState() as RootState
    const token = state.publicLinkFiles.token
    const nodeHref = state.publicLinkFiles.nodeHref
    const currentFolderId = state.publicLinkFiles.currentFolderId

    const currentItems = publicLinkCopyMoveModalSelectors.selectAll(state.publicLinkCopyMoveModal)

    if (currentFolderId === parentId) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        message: t('l_notification_cantMoveFile'),
        title: t('l_notification_samePlaceMove')
      })

      return
    }

    try {
      const sdk = getFilesSdk()

      await Promise.all(items.map(async item => {
        let newItemName

        if (item.type === NodeTypes.DIR) {
          if (isFolderExist(currentItems, item.name)) {
            newItemName = getNewFolderName(currentItems, item.name)
          }
        }

        if (item.type === NodeTypes.FILE) {
          if (isFileExist(currentItems, item.name)) {
            newItemName = getNewFileName(currentItems, item.name)
          }
        }

        await sdk.publicLinksService.updateNotMyFsNodeByLink(nodeHref, item.id, { parent_id: parentId, name: newItemName || item.name }, token)
      }))

      dispatch(publicLinkFilesActions.deleteItems(items.map(item => item.id)))
      dispatch(publicLinkFilesActions.unselectAll())

      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: t('l_notification_itemsMoved', { number: items.length })
      })
    } catch (error) {

    } finally {
      hideGlobalProgressLoader()
    }
  }
)

export const publicLinkCopyMoveModalSlice = createSlice({
  name: 'publicLinkCopyMoveModal',
  initialState: adapter.getInitialState(initialState),
  reducers: {
    setCurrentFolderId: (state, action) => {
      state.currentFolderId = action.payload
    },
    addBreadcrumb: (state, action) => {
      state.breadcrumbs.push(action.payload)
    },
    goToBreadcrumb: (state, action) => {
      const indexOfBreadcrumb = state.breadcrumbs.findIndex(breadcrumb => breadcrumb.id === action.payload.id)

      state.breadcrumbs = state.breadcrumbs.slice(0, indexOfBreadcrumb + 1)
    },
    addFolder: (state, action) => {
      adapter.setAll(state, [action.payload, ...publicLinkCopyMoveModalSelectors.selectAll(state)])
    },
    resetState: (state) => {
      Object.keys(initialState).forEach(key => {
        state[key] = initialState[key]
      })

      adapter.removeAll(state)
    }
  },
  extraReducers(builder) {
    builder
      .addCase(fetchCopyMoveNodesThunk.pending, (state) => {
        state.status = LOADING_STATUSES.LOADING
      })
      .addCase(fetchCopyMoveNodesThunk.fulfilled, (state, action) => {
        state.status = LOADING_STATUSES.SUCCEEDED
        adapter.setAll(state, action.payload)
      })
      .addCase(fetchCopyMoveNodesThunk.rejected, (state, action) => {
        state.status = LOADING_STATUSES.FAILED
        state.error = action.error.message
      })
  },
})

export default publicLinkCopyMoveModalSlice.reducer

const {
  reducer, actions
} = publicLinkCopyMoveModalSlice

export { reducer as publicLinkCopyMoveModalReducer, actions as publicLinkCopyMoveModalActions }
